// sw-protocol — typed shape of the messages the service worker
// posts to its same-origin clients on `push`.
//
// IMPORTANT: this file is the source of truth for the message type
// AND the message-type string literal. `apps/parent-app/public/sw.js`
// cannot import from .ts at runtime (it's served raw by the browser),
// so it carries a hand-copied literal of SW_MESSAGE_NOTICE_RECEIVED.
// The test in `tests/sw-listen.test.ts` reads sw.js as a string and
// asserts the literal matches the constant exported here — a tiny
// drift guard that catches a rename of either side at CI time.

/** Literal value of `message.type` the SW posts on every push. */
export const SW_MESSAGE_NOTICE_RECEIVED = "sms:sw:notice-received";

/** The full shape, including the payload fields the SW extracts from
 *  the push body. The listener treats every field except `type` as
 *  optional — a malformed payload still flips the unread state via
 *  the refetch path, even if specific fields are missing. */
export interface SWNoticeReceivedMessage {
  type: typeof SW_MESSAGE_NOTICE_RECEIVED;
  notice_id?: string;
  priority?: "normal" | "high" | "emergency";
  /** Epoch ms when the SW broadcast. Used only for debugging /
   *  ordering — the listener doesn't act on stale timestamps. */
  at: number;
}

/** Validate a message arriving on navigator.serviceWorker.message.
 *  Returns the typed shape on match, null on garbage. */
export function isNoticeReceivedMessage(
  data: unknown,
): data is SWNoticeReceivedMessage {
  if (!data || typeof data !== "object") return false;
  const d = data as Record<string, unknown>;
  return d["type"] === SW_MESSAGE_NOTICE_RECEIVED;
}

/** Window CustomEvent name re-broadcast by sw-listen.ts. Subscribers
 *  attach to this via `window.addEventListener(NOTICE_RECEIVED_EVENT, …)`. */
export const NOTICE_RECEIVED_EVENT = "sms:notice-received";

/** Window CustomEvent name dispatched by InboxView whenever it has
 *  freshly fetched notices and recomputed the unread count. Shell's
 *  chrome listens to it and surfaces the number as a badge on the
 *  Inbox tab. Event detail: `{ count: number }`. */
export const INBOX_UNREAD_EVENT = "sms:inbox-unread";

export interface InboxUnreadDetail {
  count: number;
}
